import { useCallback, useRef, useState } from 'react';

const BEFORE_CLOSE_TIMEOUT_MS = 2000;

type UseBeforeCloseReturn = {
    isProcessingClose: boolean;
    registerBeforeClose: (callback: (() => Promise<void>) | undefined) => void;
    executeBeforeClose: (onComplete: () => void) => void;
};

const useBeforeClose = (): UseBeforeCloseReturn => {
    const [isProcessingClose, setIsProcessingClose] = useState<boolean>(false);
    const beforeCloseCallbackRef = useRef<(() => Promise<void>) | undefined>(
        undefined
    );
    const isCloseInProgressRef = useRef<boolean>(false);

    const registerBeforeClose = useCallback(
        (callback: (() => Promise<void>) | undefined) => {
            beforeCloseCallbackRef.current = callback;
        },
        []
    );

    const executeBeforeClose = useCallback((onComplete: () => void): void => {
        if (isCloseInProgressRef.current) {
            return;
        }

        const callback = beforeCloseCallbackRef.current;

        if (!callback) {
            onComplete();
            return;
        }

        isCloseInProgressRef.current = true;
        setIsProcessingClose(true);

        const timeout = new Promise<void>((resolve) =>
            setTimeout(resolve, BEFORE_CLOSE_TIMEOUT_MS)
        );

        Promise.race([callback(), timeout])
            .catch(() => {})
            .finally(() => {
                isCloseInProgressRef.current = false;
                setIsProcessingClose(false);
                onComplete();
            });
    }, []);

    return {
        isProcessingClose,
        registerBeforeClose,
        executeBeforeClose,
    };
};

export default useBeforeClose;
